import {
  Component,
  Input,
  Output,
  EventEmitter,
  inject,
  HostListener,
  ViewChild,
  OnInit,
  effect,
  Injector,
  runInInjectionContext,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AuthService } from '../../../core/services/auth.service';
import { ExternalBrowserService } from '../../../core/services/external-browser.service';
import { PlatformService } from '../../../core/services/platform.service';
import { LoginModalComponent } from '../login-modal/login-modal.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-user-sidebar',
  standalone: true,
  imports: [CommonModule, LoginModalComponent, TranslateModule],
  template: `
    <!-- Sidebar Overlay -->
    <div
      class="sidebar-overlay"
      [class.sidebar-open]="isOpen"
      (click)="onClose()"
    >
      <!-- Sidebar Panel -->
      <div class="sidebar-panel" (click)="$event.stopPropagation()">
        <!-- Sidebar Header -->
        <div class="sidebar-header">
          <div class="flex items-center gap-3">
            <i class="fas fa-user-circle text-3xl text-gray-400"></i>
            <div>
              <ng-container
                *ngIf="
                  authService.isAuthenticated();
                  else notAuthenticatedHeader
                "
              >
                <h3 class="text-lg font-semibold text-gray-900">
                  {{ authService.currentUser()?.name || 'Usuario' }}
                </h3>
                <p class="text-sm text-gray-500">
                  {{ authService.currentUser()?.email }}
                </p>
              </ng-container>
              <ng-template #notAuthenticatedHeader>
                <h3 class="text-lg font-semibold text-gray-900">
                  {{ showLoginOption ? ('auth.login' | translate) : ('sidebar.menu' | translate) }}
                </h3>
                <p class="text-sm text-gray-500">
                  {{
                    showLoginOption
                      ? ('auth.accessAccount' | translate)
                      : ('nav.settings' | translate)
                  }}
                </p>
              </ng-template>
            </div>
          </div>
          <button
            (click)="onClose()"
            class="close-btn"
            aria-label="Cerrar menú"
            type="button"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- Sidebar Navigation -->
        <nav class="sidebar-nav">
          <!-- Authenticated User Actions -->
          <ng-container
            *ngIf="authService.isAuthenticated(); else notAuthenticatedActions"
          >
            <!-- User Actions -->
            <div class="sidebar-section">
              <a (click)="navigateToSettings()" class="sidebar-link">
                <i class="fas fa-cog"></i>
                <span>{{ 'nav.settings' | translate }}</span>
              </a>
            </div>

            <!-- Admin Panel (only for admins) -->
            <ng-container *ngIf="authService.isAdmin()">
              <div class="sidebar-section">
                <a
                  (click)="toggleAdminPanel()"
                  class="sidebar-link admin-link"
                  [class.expanded]="isAdminPanelExpanded"
                >
                  <i class="fas fa-user-shield"></i>
                  <span>{{ 'sidebar.adminPanel' | translate }}</span>
                  <i
                    class="fas fa-chevron-down ml-auto transition-transform duration-200"
                    [class.rotate-180]="isAdminPanelExpanded"
                  ></i>
                </a>
              </div>
              <!-- Admin Panel Dropdown -->
              <div
                class="admin-panel-dropdown"
                [class.expanded]="isAdminPanelExpanded"
              >
                <a
                  (click)="
                    navigateToAdmin('dashboard'); $event.preventDefault()
                  "
                  class="sidebar-link admin-sub-link"
                  [class.active]="isActiveAdminRoute('dashboard')"
                  href="#"
                >
                  <i class="fas fa-home"></i>
                  <span>Home</span>
                </a>
                <a
                  (click)="
                    navigateToAdmin('categories'); $event.preventDefault()
                  "
                  class="sidebar-link admin-sub-link"
                  [class.active]="isActiveAdminRoute('categories')"
                  href="#"
                >
                  <i class="fas fa-tags"></i>
                  <span>Categorias</span>
                </a>
                <a
                  (click)="navigateToAdmin('brands'); $event.preventDefault()"
                  class="sidebar-link admin-sub-link"
                  [class.active]="isActiveAdminRoute('brands')"
                  href="#"
                >
                  <i class="fas fa-store"></i>
                  <span>Restaurantes</span>
                </a>
                <a
                  (click)="
                    navigateToAdmin('restaurants'); $event.preventDefault()
                  "
                  class="sidebar-link admin-sub-link"
                  [class.active]="isActiveAdminRoute('restaurants')"
                  href="#"
                >
                  <i class="fas fa-utensils"></i>
                  <span>Locales</span>
                </a>
                <a
                  (click)="navigateToAdmin('offers'); $event.preventDefault()"
                  class="sidebar-link admin-sub-link"
                  [class.active]="isActiveAdminRoute('offers')"
                  href="#"
                >
                  <i class="fas fa-ticket-alt"></i>
                  <span>Ofertas</span>
                </a>
                <a
                  (click)="navigateToAdmin('menus'); $event.preventDefault()"
                  class="sidebar-link admin-sub-link"
                  [class.active]="isActiveAdminRoute('menus')"
                  href="#"
                >
                  <i class="fas fa-book-open"></i>
                  <span>{{ 'nav.menus' | translate }}</span>
                </a>
                <a
                  (click)="
                    navigateToAdmin('locations'); $event.preventDefault()
                  "
                  class="sidebar-link admin-sub-link"
                  [class.active]="isActiveAdminRoute('locations')"
                  href="#"
                >
                  <i class="fas fa-map-marker-alt"></i>
                  <span>Ubicaciones</span>
                </a>
                <a
                  (click)="
                    navigateToAdmin('extractions'); $event.preventDefault()
                  "
                  class="sidebar-link admin-sub-link"
                  [class.active]="isActiveAdminRoute('extractions')"
                  href="#"
                >
                  <i class="fas fa-database"></i>
                  <span>Extracciones</span>
                </a>
                <a
                  (click)="navigateToAdmin('users'); $event.preventDefault()"
                  class="sidebar-link admin-sub-link"
                  [class.active]="isActiveAdminRoute('users')"
                  href="#"
                >
                  <i class="fas fa-users"></i>
                  <span>Usuarios</span>
                </a>
                <a
                  (click)="navigateToAdmin('requests'); $event.preventDefault()"
                  class="sidebar-link admin-sub-link"
                  [class.active]="isActiveAdminRoute('requests')"
                  href="#"
                >
                  <i class="fas fa-inbox"></i>
                  <span>Peticiones</span>
                </a>
                <a
                  (click)="
                    navigateToAdmin('statistics'); $event.preventDefault()
                  "
                  class="sidebar-link admin-sub-link"
                  [class.active]="isActiveAdminRoute('statistics')"
                  href="#"
                >
                  <i class="fas fa-chart-bar"></i>
                  <span>{{ 'sidebar.usageStats' | translate }}</span>
                </a>
                <a
                  (click)="
                    navigateToAdmin('database'); $event.preventDefault()
                  "
                  class="sidebar-link admin-sub-link"
                  [class.active]="isActiveAdminRoute('database')"
                  href="#"
                >
                  <i class="fas fa-server"></i>
                  <span>Base de Datos</span>
                </a>
                <a
                  (click)="
                    navigateToAdmin('submissions'); $event.preventDefault()
                  "
                  class="sidebar-link admin-sub-link"
                  [class.active]="isActiveAdminRoute('submissions')"
                  href="#"
                >
                  <i class="fas fa-camera"></i>
                  <span>Ofertas Usuarios</span>
                </a>
                <a
                  (click)="
                    navigateToAdmin('raffles'); $event.preventDefault()
                  "
                  class="sidebar-link admin-sub-link"
                  [class.active]="isActiveAdminRoute('raffles')"
                  href="#"
                >
                  <i class="fas fa-gift"></i>
                  <span>Sorteos</span>
                </a>
              </div>
            </ng-container>

            <div class="sidebar-divider"></div>

            <div class="sidebar-section">
              <button
                (click)="handleLogout()"
                class="sidebar-link logout-link"
                type="button"
              >
                <i class="fas fa-sign-out-alt"></i>
                <span>{{ 'auth.logout' | translate }}</span>
              </button>
            </div>
          </ng-container>

          <!-- Not Authenticated Actions (login button only when sign-in is available) -->
          <ng-template #notAuthenticatedActions>
            <div class="sidebar-section" *ngIf="showLoginOption">
              <button
                (click)="handleLogin()"
                class="sidebar-link login-link"
                type="button"
              >
                <i class="fas fa-sign-in-alt"></i>
                <span>{{ 'auth.login' | translate }}</span>
              </button>
            </div>
          </ng-template>

          <!-- Language Toggle -->
          <div class="sidebar-divider"></div>
          <div class="sidebar-section" style="padding: 0.75rem 1.5rem;">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2 text-gray-600">
                <i class="fas fa-globe" style="width: 1.2rem; text-align: center;"></i>
                <span class="text-sm font-medium">{{ 'sidebar.language' | translate }}</span>
              </div>
              <div class="flex bg-gray-100 rounded-lg p-0.5">
                <button
                  type="button"
                  (click)="switchLanguage('es')"
                  class="px-3 py-1 text-xs font-semibold rounded-md transition-all"
                  [class]="currentLang === 'es'
                    ? 'bg-white text-pink-600 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'"
                >
                  ES
                </button>
                <button
                  type="button"
                  (click)="switchLanguage('en')"
                  class="px-3 py-1 text-xs font-semibold rounded-md transition-all"
                  [class]="currentLang === 'en'
                    ? 'bg-white text-pink-600 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'"
                >
                  EN
                </button>
              </div>
            </div>
          </div>

          <!-- Legal Links Section -->
          <div class="sidebar-divider"></div>
          <div class="sidebar-section-title">{{ 'sidebar.legalInfo' | translate }}</div>

          <a
            (click)="navigateToLegal('terminos-servicio')"
            class="sidebar-link legal-link"
          >
            <i class="fas fa-file-contract"></i>
            <span>{{ 'sidebar.termsOfService' | translate }}</span>
          </a>

          <a
            (click)="navigateToLegal('politica-de-privacidad')"
            class="sidebar-link legal-link"
          >
            <i class="fas fa-shield-alt"></i>
            <span>{{ 'sidebar.privacyPolicy' | translate }}</span>
          </a>

          <a
            (click)="navigateToLegal('politica-cookies')"
            class="sidebar-link legal-link"
          >
            <i class="fas fa-cookie"></i>
            <span>{{ 'sidebar.cookiePolicy' | translate }}</span>
          </a>

          <a
            (click)="navigateToLegal('derechos-gdpr')"
            class="sidebar-link legal-link"
          >
            <i class="fas fa-users"></i>
            <span>Derechos GDPR</span>
          </a>
        </nav>
      </div>
    </div>

    <!-- Login Modal -->
    <app-login-modal #loginModal></app-login-modal>
  `,
  styles: [
    `
      .sidebar-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background-color: rgba(0, 0, 0, 0.5);
        z-index: 60;
        opacity: 0;
        visibility: hidden;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        backdrop-filter: blur(4px);
      }

      .sidebar-overlay.sidebar-open {
        opacity: 1;
        visibility: visible;
      }

      .sidebar-panel {
        position: absolute;
        top: 0;
        right: 0;
        width: 320px;
        max-width: 85vw;
        height: 100vh;
        background: white;
        box-shadow: -4px 0 24px rgba(0, 0, 0, 0.15);
        transform: translateX(100%);
        transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        overflow-y: auto;
        display: flex;
        flex-direction: column;
      }

      .sidebar-overlay.sidebar-open .sidebar-panel {
        transform: translateX(0);
      }

      .sidebar-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 1.5rem;
        /* Safe area inset for iOS/Android native status bar */
        padding-top: calc(
          1.5rem + var(--safe-area-inset-top, env(safe-area-inset-top, 0px))
        );
        border-bottom: 1px solid #e5e7eb;
        flex-shrink: 0;
      }

      .close-btn {
        background: none;
        border: none;
        cursor: pointer;
        color: #6b7280;
        padding: 8px;
        border-radius: 6px;
        transition: all 0.2s ease;
        font-size: 1.25rem;
        width: 36px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .close-btn:hover {
        background-color: #f3f4f6;
        color: #374151;
      }

      .sidebar-nav {
        padding-bottom: 1rem 0;
        flex: 1;
        overflow-y: auto;
      }

      .sidebar-section {
        padding: 0.5rem 0;
      }

      .sidebar-link {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 1rem 1.5rem;
        color: #374151;
        text-decoration: none;
        font-weight: 500;
        font-size: 1rem;
        transition: all 0.2s ease;
        border-left: 4px solid transparent;
        cursor: pointer;
        width: 100%;
        background: none;
        border-top: none;
        border-right: none;
        border-bottom: none;
        text-align: left;
      }

      .sidebar-link:hover {
        background-color: rgba(233, 29, 99, 0.05);
        color: #e91d63;
        border-left-color: #e91d63;
      }

      .sidebar-link i {
        width: 1.2rem;
        text-align: center;
        flex-shrink: 0;
      }

      .sidebar-link.logout-link {
        color: #dc2626;
      }

      .sidebar-link.logout-link:hover {
        background-color: rgba(220, 38, 38, 0.05);
        color: #dc2626;
        border-left-color: #dc2626;
      }

      .sidebar-link.login-link {
        color: #e91d63;
        font-weight: 600;
      }

      .sidebar-link.login-link:hover {
        background-color: rgba(233, 29, 99, 0.1);
        color: #e91d63;
        border-left-color: #e91d63;
      }

      .sidebar-link.admin-link {
        color: #7c3aed;
        font-weight: 600;
        position: relative;
        display: flex;
        align-items: center;
      }

      .sidebar-link.admin-link .fa-chevron-down {
        margin-left: auto;
        font-size: 0.75rem;
        transition: transform 0.2s ease;
      }

      .sidebar-link.admin-link .fa-chevron-down.rotate-180 {
        transform: rotate(180deg);
      }

      .sidebar-link.admin-link:hover {
        background-color: rgba(124, 58, 237, 0.1);
        color: #7c3aed;
        border-left-color: #7c3aed;
      }

      .admin-panel-dropdown {
        max-height: 0;
        overflow: hidden;
        transition: max-height 0.3s ease-out;
      }

      .admin-panel-dropdown.expanded {
        max-height: 100vh; /* Use viewport height to ensure all items fit */
      }

      .sidebar-link.admin-sub-link {
        padding-left: 3rem;
        font-size: 0.9375rem;
        color: #6b7280;
        font-weight: 400;
      }

      .sidebar-link.admin-sub-link:hover {
        background-color: rgba(124, 58, 237, 0.05);
        color: #7c3aed;
        border-left-color: #7c3aed;
      }

      .sidebar-link.admin-sub-link.active {
        background-color: rgba(124, 58, 237, 0.15);
        color: #7c3aed;
        font-weight: 600;
        border-left: 3px solid #7c3aed;
      }

      .sidebar-link.admin-sub-link.active:hover {
        background-color: rgba(124, 58, 237, 0.2);
        color: #7c3aed;
      }

      .sidebar-link.legal-link {
        font-size: 0.9375rem;
        padding: 0.75rem 1.5rem;
        color: #6b7280;
      }

      .sidebar-link.legal-link:hover {
        background-color: rgba(233, 29, 99, 0.03);
        color: #e91d63;
      }

      .sidebar-divider {
        height: 1px;
        background-color: #e5e7eb;
      }

      .sidebar-section-title {
        padding: 0.5rem 1.5rem;
        font-size: 0.875rem;
        font-weight: 600;
        color: #9ca3af;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }

      /* Smooth scrollbar */
      .sidebar-panel {
        scrollbar-width: thin;
        scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
      }

      .sidebar-panel::-webkit-scrollbar {
        width: 6px;
      }

      .sidebar-panel::-webkit-scrollbar-track {
        background: transparent;
      }

      .sidebar-panel::-webkit-scrollbar-thumb {
        background-color: rgba(0, 0, 0, 0.2);
        border-radius: 3px;
      }
    `,
  ],
})
export class UserSidebarComponent implements OnInit {
  @Input() isOpen = false;
  @Output() close = new EventEmitter<void>();
  @ViewChild('loginModal') loginModal?: LoginModalComponent;

  authService = inject(AuthService);
  private router = inject(Router);
  private injector = inject(Injector);
  private externalBrowser = inject(ExternalBrowserService);
  private platformService = inject(PlatformService);
  private translateService = inject(TranslateService);

  isNativePlatform = this.platformService.isNative();
  showLoginOption = !this.isNativePlatform && (environment.googleSignInEnabled ?? false);
  currentLang = this.translateService.currentLang || this.translateService.defaultLang || 'es';

  // Admin panel dropdown state
  isAdminPanelExpanded = false;
  currentRoute = '';

  ngOnInit(): void {
    // Initialize current route
    this.updateCurrentRoute();

    // Check if we should expand admin panel on init
    this.checkAndExpandAdminPanel();

    // Listen to route changes
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateCurrentRoute();
        // When route changes, check if we need to expand admin panel
        if (this.isOpen) {
          // Use requestAnimationFrame to ensure DOM is ready
          requestAnimationFrame(() => {
            this.checkAndExpandAdminPanel();
          });
        } else {
          this.checkAndExpandAdminPanel();
        }
      });

    // Watch for sidebar opening to check if we should expand admin panel
    // Use runInInjectionContext to allow effect() to run in ngOnInit
    runInInjectionContext(this.injector, () => {
      effect(() => {
        if (this.isOpen) {
          // Use requestAnimationFrame and a small delay to ensure route is updated and DOM is ready
          requestAnimationFrame(() => {
            setTimeout(() => {
              this.updateCurrentRoute();
              this.checkAndExpandAdminPanel();
            }, 50);
          });
        }
      });
    });
  }

  private updateCurrentRoute(): void {
    this.currentRoute = this.router.url;
  }

  private checkAndExpandAdminPanel(): void {
    // Update current route first
    this.updateCurrentRoute();

    // Check if current route is an admin route (including /admin with no sub-route)
    if (this.currentRoute.startsWith('/admin')) {
      // Extract the admin category from the route
      const routeAfterAdmin = this.currentRoute.replace('/admin', '').trim();

      // If we're on /admin or any admin sub-route, expand the panel
      // This includes /admin (dashboard) and /admin/category paths
      if (routeAfterAdmin === '' || routeAfterAdmin.startsWith('/')) {
        if (!this.isAdminPanelExpanded) {
          this.isAdminPanelExpanded = true;
        }
      }
    } else {
      // If we're not on an admin route, only collapse if sidebar is closed
      // This prevents collapsing when navigating away while sidebar is open
      if (!this.isOpen) {
        this.isAdminPanelExpanded = false;
      }
    }
  }

  isActiveAdminRoute(category: string): boolean {
    const routePath = `/admin/${category}`;
    // Check exact match or if current route starts with the category path
    return (
      this.currentRoute === routePath ||
      (this.currentRoute.startsWith(routePath) &&
        this.currentRoute.length > routePath.length &&
        this.currentRoute[routePath.length] === '/')
    );
  }

  @HostListener('window:resize')
  onResize() {
    // Close sidebar on desktop resize (optional behavior)
    if (window.innerWidth > 768 && this.isOpen) {
      // You can decide if you want this behavior
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeKey() {
    if (this.isOpen) {
      this.onClose();
    }
  }

  onClose(): void {
    this.close.emit();
    // Restore body scroll
    document.body.style.overflow = '';
  }

  handleLogout(): void {
    this.authService
      .logout()
      .then(() => {
        this.onClose();
        // Redirect to offers page after logout
        this.router.navigate(['/offers']).then(() => {
          // Small delay to ensure navigation completes before potential page reload
          setTimeout(() => {
            window.location.reload();
          }, 100);
        });
      })
      .catch((error) => {
        console.error('Logout error:', error);
        // Even if logout fails, close sidebar and navigate
        this.onClose();
        this.router.navigate(['/offers']);
      });
  }

  handleLogin(): void {
    this.onClose();
    // Open login modal after sidebar closes
    setTimeout(() => {
      this.loginModal?.open();
    }, 300);
  }

  navigateToSettings(): void {
    this.onClose();
    this.router.navigate(['/settings']);
  }

  toggleAdminPanel(): void {
    this.isAdminPanelExpanded = !this.isAdminPanelExpanded;
  }

  navigateToAdmin(category?: string): void {
    if (category) {
      // Navigate to specific admin category
      console.log('Navigating to admin category:', category);
      this.router.navigate(['/admin', category]).then(
        (success) => {
          if (success) {
            console.log('Navigation successful to /admin/' + category);
            this.onClose();
            this.isAdminPanelExpanded = false;
          } else {
            console.error('Navigation failed to /admin/' + category);
          }
        },
        (error) => {
          console.error('Navigation error to /admin/' + category, error);
        },
      );
    } else {
      // Navigate to admin home if no category specified
      this.router.navigate(['/admin']).then(
        (success) => {
          if (success) {
            this.onClose();
            this.isAdminPanelExpanded = false;
          }
        },
        (error) => {
          console.error('Navigation error to /admin', error);
        },
      );
    }
  }

  switchLanguage(lang: string): void {
    this.translateService.use(lang);
    this.currentLang = lang;
    localStorage.setItem('gastronauta_lang', lang);
  }

  navigateToLegal(legalPage: string): void {
    this.onClose();
    // Open legal page in external browser (Safari/Chrome) instead of in-app WebView
    this.externalBrowser.openUrl(
      `https://gastronauta.app/landing/legal/${legalPage}`,
    );
  }
}
